import { ReactElement } from 'react';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import { Link } from 'react-router-dom';
import { ErrorOutline } from '@mui/icons-material';
import { Grid } from '@mui/material';
import styles from './Item.module.scss';
import BloodGlucose from '~/ui/components/reusable/Reading/BloodGlucose';
import BloodPressure from '~/ui/components/reusable/Reading/BloodPressure';
import PulseOximeter from '~/ui/components/reusable/Reading/PulseOximeter';
import WeightScale from '~/ui/components/reusable/Reading/WeightScale';
import { PATIENT_DASHBOARD } from '~/ui/constants/paths';
import { IPagePermissions } from '~/ui/pages/Triage/types';
import { isoToFormat } from '~/utils/date';
import { ITriageRequestList } from '~/services/api/triage/types';
import { IBloodPressureReading } from '~/services/api/readings/types';

interface IProps {
  request: ITriageRequestList;
  permissions: IPagePermissions;
  programId: number;
  setSelectedNoteId: (id: number) => void;
  onBloodPressureUpdated: (data: IBloodPressureReading) => void;
}

const Item = ({
  request,
  permissions,
  programId,
  setSelectedNoteId,
  onBloodPressureUpdated,
}: IProps): ReactElement => (
  <TableRow>
    {programId === null && <TableCell className={styles.cell}>{request.programName}</TableCell>}
    <TableCell className={styles.cell}>
      {permissions.canManagePatients ? (
        <Link
          to={PATIENT_DASHBOARD.replace(':id', String(request.patientId))}
          className={styles.link}
        >
          {request.patientFullName}
        </Link>
      ) : (
        request.patientFullName
      )}
    </TableCell>
    <TableCell className={styles.cell}>
      <Grid container alignItems="center">
        <Grid item>
          <div
            className={styles.link}
            onClick={() => setSelectedNoteId(request.noteId)}
            role="presentation"
          >
            {request.issueName}
          </div>
        </Grid>
        <Grid item>
          {request.isHighPriority && <ErrorOutline className={styles.highPriorityIcon} />}
        </Grid>
      </Grid>
    </TableCell>
    <TableCell>{isoToFormat(request.noteCreatedAt, 'dd MMM y')}</TableCell>
    <TableCell width={260}>
      <div className={styles.latestReadingWidgets}>
        {request.latestReadings.bloodPressure && (
          <BloodPressure
            reading={request.latestReadings.bloodPressure}
            rules={request.notificationRules}
            timezone={request.patientTimezone}
            onUpdated={onBloodPressureUpdated}
          />
        )}
        {request.latestReadings.weightScale && (
          <WeightScale
            reading={request.latestReadings.weightScale}
            rules={request.notificationRules}
            timezone={request.patientTimezone}
          />
        )}
        {request.latestReadings.pulseOximeter && (
          <PulseOximeter
            reading={request.latestReadings.pulseOximeter}
            rules={request.notificationRules}
            timezone={request.patientTimezone}
          />
        )}
        {request.latestReadings.bloodGlucose && (
          <BloodGlucose
            reading={request.latestReadings.bloodGlucose}
            rules={request.notificationRules}
            timezone={request.patientTimezone}
          />
        )}
      </div>
    </TableCell>
  </TableRow>
);

export default Item;
